/**
 * Pick many things from a long list (docs/03 §3).
 *
 * Built because the one-at-a-time version does not survive contact with the
 * job: a new coordinator needs forty properties, and Matt's read of the old
 * control was exactly right — "this would be cumbersome". Forty round trips
 * through a dialog is not a slow feature, it is a feature nobody uses, and the
 * work moves to a spreadsheet somebody emails around instead.
 *
 * Three things make it usable at that size, and each one is the answer to a way
 * the naive version fails:
 *
 *  - **Search narrows, selection persists.** Type "north", tick two, clear the
 *    search, type "west", tick three — you still have five. A picker that drops
 *    selections when the filter changes silently loses work.
 *  - **Select all applies to what you are LOOKING AT**, and says how many that
 *    is. "Select all 40 shown" is a promise the label can keep; a bare "select
 *    all" on a filtered list is ambiguous in the one situation where it matters.
 *  - **The count is always on screen.** With a scrolling list, the number
 *    selected is the only way to know what the confirm button will do.
 */

import { Check } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { Button } from './Button.js';
import { TextField } from './fields.js';

export interface PickerOption {
  readonly id: string;
  readonly label: string;
  /**
   * Second line — a city, a status, whatever distinguishes near-duplicates.
   * `| undefined` explicitly, so a caller can map a nullable column straight
   * onto it without a conditional-spread dance at every call site.
   */
  readonly detail?: string | undefined;
}

export function MultiSelectPicker({
  options,
  selected,
  onChange,
  searchLabel,
  emptyLabel,
  /**
   * Names the unit in the counts, BOTH forms. Required rather than derived: the
   * count line read "1 properties selected", and no suffix rule turns
   * "properties" into "property" or "people" into "person". Making the caller
   * say both is the only version that cannot be subtly wrong.
   */
  noun,
}: {
  readonly options: readonly PickerOption[];
  readonly selected: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly searchLabel: string;
  readonly emptyLabel: string;
  readonly noun: { readonly one: string; readonly many: string };
}): ReactNode {
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.detail ?? '').toLowerCase().includes(needle),
    );
  }, [options, search]);

  const chosen = new Set(selected);
  const shownIds = shown.map((option) => option.id);
  const allShownChosen = shownIds.length > 0 && shownIds.every((id) => chosen.has(id));

  const toggle = (id: string): void => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <div className="flex flex-col gap-3">
      <TextField
        label={searchLabel}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        placeholder="Type to narrow the list"
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p aria-live="polite" className="text-meta text-ink2">
          {selected.length === 0
            ? `No ${noun.many} selected`
            : `${String(selected.length)} ${selected.length === 1 ? noun.one : noun.many} selected`}
          {search.trim() === '' ? null : ` · ${String(shown.length)} shown`}
        </p>
        <span className="flex gap-2">
          {shownIds.length === 0 ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                // Applies to the CURRENT filter, both ways, and the label says
                // so — the whole point is that "all" is unambiguous here.
                const next = new Set(chosen);
                for (const id of shownIds) {
                  if (allShownChosen) next.delete(id);
                  else next.add(id);
                }
                onChange([...next]);
              }}
            >
              {allShownChosen
                ? `Clear the ${String(shownIds.length)} shown`
                : `Select all ${String(shownIds.length)} shown`}
            </Button>
          )}
          {selected.length === 0 ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                onChange([]);
              }}
            >
              Clear selection
            </Button>
          )}
        </span>
      </div>

      {/* Bounded height with its own scroll: the dialog must not grow past the
          viewport on a list of hundreds, and the footer has to stay reachable. */}
      <ul className="max-h-72 overflow-y-auto rounded-ctl border border-hairline">
        {shown.length === 0 ? (
          <li className="px-3 py-4 text-body text-ink2">{emptyLabel}</li>
        ) : (
          shown.map((option) => {
            const isChosen = chosen.has(option.id);
            return (
              <li key={option.id} className="border-b border-hairline last:border-b-0">
                <button
                  type="button"
                  aria-pressed={isChosen}
                  onClick={() => {
                    toggle(option.id);
                  }}
                  className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-700"
                >
                  {/* A real check rather than colour alone (docs/03 §8). */}
                  <span
                    aria-hidden
                    className={
                      isChosen
                        ? 'flex size-5 shrink-0 items-center justify-center rounded-sm bg-brand-700 text-on-brand'
                        : 'flex size-5 shrink-0 items-center justify-center rounded-sm border border-hairline'
                    }
                  >
                    {isChosen ? <Check size={14} /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-body text-ink">{option.label}</span>
                    {option.detail === undefined ? null : (
                      <span className="block truncate text-meta text-ink2">{option.detail}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
